import { types, flow, getSnapshot, getRoot } from "mobx-state-tree"
import {
  STATES,
  PARKING_STATUS,
  MAX_FILE_SIZE_MB,
  PARKING_FLOW_TYPE,
  VALID_PARKING_KEYS
} from "~/utilities/constants"
import * as flowTicketService from "~/services/parking/flow"
import * as parkingTicketService from "~/services/parking/ticket"
import buildPersistedChangesStore from "~/stores/models/persistedChanges"
import buildCustomValidationStore from "~/stores/models/customValidation"
import { getUserSessionToken } from "./parkingConfig"
import { get, getByTicketIDAndEmail } from "~/services/parking/ticket"

import PersistedImage from '~/stores/models/persistedParkingEvidence'
import * as FileTools from '~/utilities/files'

const TicketResultModel = types
  .model("TicketResult", {
    ticketID: types.identifier,
    summons_number: types.optional(types.string, ""),
    objectId: types.optional(types.string, ""),
    plate: types.maybe(types.string),
    plateId: types.maybe(types.string),
    plateNumber: types.maybe(types.string),
    state: types.maybe(types.enumeration(Object.keys(STATES))),
    alert: types.maybe(types.string),
    amountDue: types.maybe(types.string),
    amountPaid: types.maybe(types.string),
    fineAmount: types.maybe(types.string),
    inHearingMessage: types.maybe(types.string),
    inJudgmentMessage: types.maybe(types.string),
    interestAmount: types.optional(types.number, 0),
    penaltyAmount: types.optional(types.number, 0),
    penalty_amount: types.maybe(types.string),
    isLessee: types.maybe(types.string),
    isPastDueMessage: types.maybe(types.string),
    issuedOn: types.maybe(types.string),
    issue_date: types.maybe(types.string),
    make: types.maybe(types.string),
    reductionAmount: types.maybe(types.string),
    violationLocationCounty: types.maybe(types.string),
    violationLocationModifier: types.maybe(types.string),
    violationLocationStreet: types.maybe(types.string),
    violationName: types.maybe(types.string),
    violationType: types.maybe(types.string),
    currentFine: types.maybe(types.string),
    violationCode: types.maybe(types.string),
    violation_code: types.maybe(types.string),
    vehicleType: types.maybe(types.string),
    license_type: types.maybe(types.string),
    status: types.maybeNull(types.enumeration(Object.values(PARKING_STATUS))), // set on backend if ticket exists in certain statuses
    fightable: types.optional(types.boolean, false), // virtual field on backend
    blocked: types.optional(types.boolean, false),
    isPaid: types.optional(types.boolean, false), // set on backend if ticket has isPaid
    processPayment: types.optional(types.boolean, false), // set on backend if ticket has processPayment
    isUserManaged: types.optional(types.boolean, false),
    vehicleRegistrant: types.maybeNull(types.string),
    registrantAddress: types.maybeNull(types.string),
    persistedEvidenceImages:   types.optional(types.array(PersistedImage),[]),
  })
  .views(self => ({
    get nonActionable() {
      return self.status === "pending"
        || self.blocked === true
      || self.status === "processing"
      || self.status === "dismissed"
      // || self.status === "cannot be disputed"
      || (self.status === "has to be paid"
      && (self.processPayment === true
      || self.isPaid === true))
    },
    get isFightableTicket() {
      return (
        self.fightable
        // && self.status !== "pending"
        // && self.status !== "processing"
        // && self.status !== "dismissed"
        && self.status !== "cannot be disputed"
        // && self.status !== "has to be paid"
        && self.processPayment === false
        && self.isPaid === false
      )
    },
    get isPayableTicket() {
      return (
        self.status !== "pending"
        && self.status !== "processing"
        && self.status !== "dismissed"
      )
    },
    get dollarAmountDue() {
      const n = Number(self.amountDue)
      // amountDue is empty on windshield tickets until the user types it; avoid $NaN.
      return Number.isFinite(n) ? `$${n.toFixed(2)}` : ''
    },
    get isWindshield() {
      // Freshly issued NYC ticket not yet fully in CityPay; user enters the fine.
      return /windshield/i.test(self.violationName || '')
    },
    // Human-readable violation location built from up to three optional parts:
    // modifier ("At the intersection of"), street ("TRINITY AVE"), county
    // ("Bronx"). Each part may be missing; modifier+street are space-joined,
    // county is comma-appended. Empty string when all parts are absent.
    get violationLocation() {
      const streetPart = [
        self.violationLocationModifier,
        self.violationLocationStreet,
      ]
        .filter(Boolean)
        .join(" ")
      const parts = [streetPart, self.violationLocationCounty].filter(Boolean)
      return parts.join(", ")
    },
    get nonActionableMessage() {
      switch (self.status) {
        case PARKING_STATUS.fromRobot:
        case PARKING_STATUS.temp:
        case PARKING_STATUS.pending:
        case PARKING_STATUS.processing:
          return 'This ticket is already being fought by WinIt!'
        case PARKING_STATUS.hold:
        case PARKING_STATUS.closed:
          return 'This ticket was submitted to WinIt but has been canceled or closed.'
        case PARKING_STATUS.dismissed:
          return "This ticket has been dismissed by WinIt!"
        case PARKING_STATUS['has to be paid']:
          if (self.isPaid) {
            return "This ticket is already paid for."
          }
          if (self.processPayment) {
            return "A payment for this ticket is currently processing."
          }
          if (self.blocked) {
            return "Sorry you cannot pay this ticket with Winit, you must pay in person."
          }
          return undefined
        case PARKING_STATUS['cannot be disputed']:
          return 'Sorry, we can not fight this ticket here. Please contact us for more information.'
        default:
          return 'Sorry, we can not fight this ticket here. Please contact us for more information.'
      }
    }
  }))
  .actions(self => ({
    saveTicket: flow(function* saveTicket(flowType, sessionToken, userEmail) {
      const isPayFlow = flowType === PARKING_FLOW_TYPE.pay
      const isRobotPayFlow = flowType === PARKING_FLOW_TYPE.robotpay
      const isFightFlow = flowType === PARKING_FLOW_TYPE.fight
      const data = self.buildDataUpdateObject(VALID_PARKING_KEYS)
      if (!sessionToken && data) { // if we don't have a session token for payNYC flow where users did not sign in
        data.userEmail = userEmail;
      }
      let saveResponse
      if (self.isPersisted && data) {
        data.processPayment = false // set to false we certainly don't want to pass this flag to true if we are updating an existing ticket (will be done later by the backend)
        // if ticket existed & we have changes update it
        saveResponse = yield parkingTicketService.update(
          self.objectId,
          data,
          sessionToken
        )
      } else if (!self.isPersisted && data) {
        // if ticket is not saved and we have updates save it
        let action
        if (isPayFlow || isRobotPayFlow) {
          action = "createToPay"
        } else if (isFightFlow) {
          action = "createToFight"
        }
        saveResponse = yield flowTicketService[action](data, sessionToken)
      }

      // if we have update
      if (saveResponse) {
        if(isFightFlow) {
          yield self.uploadAllEvidenceImages(saveResponse.objectId)
        }
        if (saveResponse.objectId) self.setObjectId(saveResponse.objectId)
        self.updateData(saveResponse)
        self.setLastUpdate(saveResponse)
      }
    }),
    updateData(data) {
      VALID_PARKING_KEYS.forEach(key => {
        if (data[key]) self[key] = data[key]
      })
    },
    setAmountDue(value) {
      self.amountDue = value
    },
    setObjectId(value) {
      self.objectId = value
    },
    fetchTicket: flow(function* handleTicketFetch() {
      try {
        const sessionToken = getUserSessionToken(self)
        const { email } = getRoot(self).auth.user
        // PayNyc guest / stale-session refetch: fall back to the public
        // ticketID + email lookup when there is no usable session token.
        const ticket = sessionToken
          ? yield get(self.objectId, sessionToken)
          : yield getByTicketIDAndEmail(self.ticketID, email)
        self.updateData(ticket)
      } catch (error) {
        throw new Error(`error re-fetching tickets: ${error}`)
      }
    }),
    handleEvidenceImageDrop: flow(function* imageDropGen(acceptedFiles) {
      if(!acceptedFiles || !acceptedFiles.length) return false
      const snapshot = getSnapshot(self)
      // Prevent adding more than the maximum # of images.
      const ADDITIONAL_IMAGES_ALLOWED =
        3 -
        snapshot.persistedEvidenceImages.length
      if (ADDITIONAL_IMAGES_ALLOWED <= 0) return false

      let additionalImages
      if (acceptedFiles.length > ADDITIONAL_IMAGES_ALLOWED) {
        additionalImages = acceptedFiles.slice(0, ADDITIONAL_IMAGES_ALLOWED)
      } else {
        additionalImages = acceptedFiles
      }

      // remove large files
      const validFiles = FileTools.removeLargeFilesFromArray(
        additionalImages,
        MAX_FILE_SIZE_MB
      )

      // Loop through the files to add them evidence store
      for (let x = 0; x < validFiles.length; x += 1) {
        const file = validFiles[x]
        self.persistedEvidenceImages.push({})
        const EVIDENCE_IMAGE_COUNT = self.persistedEvidenceImages.length
        const currentPersistedImage =
          self.persistedEvidenceImages[EVIDENCE_IMAGE_COUNT - 1]
        yield currentPersistedImage.attachImageFromFile(file)
        currentPersistedImage.setType(`optional${EVIDENCE_IMAGE_COUNT}`)
      }
      return true
    }),
    uploadAllEvidenceImages: flow(function* uploadAllEvidenceImages(objectId) {
      const uploads = []
      for(let i = 0; i < self.persistedEvidenceImages.length; i += 1) {
        const evidenceImage = self.persistedEvidenceImages[i]
        const result = yield evidenceImage.setEvidenceImage(
          objectId
        )
        uploads.push(result)
      }
      return uploads
    }),
    removeLocalEvidenceImage(id) {
      self.persistedEvidenceImages = self.persistedEvidenceImages.filter(
        item => item.id !== id
      )
    }
  }))


const PersistedChangesStore = buildPersistedChangesStore({})
const CustomValidationStore = buildCustomValidationStore({
  validationObject: {}
})

const TicketResult = types.compose(
  TicketResultModel,
  PersistedChangesStore,
  CustomValidationStore
)

export default TicketResult
